import { useEffect, useState } from 'react';

import { getInviteEmployee, type W2InvitePrefill } from '../api/w2';

/**
 * Resolve the roster invite pre-fill for the register route's `?emp=<ref>`.
 *
 *   no ref        → settled synchronously with `data: null` (no fetch, no
 *                   loading flash) so the B2C register form renders exactly as
 *                   today, empty + fully editable.
 *   ref present   → 'loading' until the exchange settles, then `data` = the
 *                   roster row (200) or `null` (404 / failure). `getInviteEmployee`
 *                   never throws — the ref is an enhancement, never a blocker.
 */
export type InvitePrefillState =
  | { phase: 'loading' }
  | { phase: 'ready'; data: W2InvitePrefill | null };

export function useInvitePrefill(
  token: string,
  ref: string | null
): InvitePrefillState {
  const [state, setState] = useState<InvitePrefillState>(() =>
    ref ? { phase: 'loading' } : { phase: 'ready', data: null }
  );

  useEffect(() => {
    if (!ref) {
      setState({ phase: 'ready', data: null });
      return;
    }
    let active = true;
    setState({ phase: 'loading' });
    void getInviteEmployee(token, ref).then((data) => {
      if (active) setState({ phase: 'ready', data });
    });
    return () => {
      active = false;
    };
  }, [token, ref]);

  return state;
}
